// =============================================================================
// Per-device register profile (faithful to the legacy POS My.Settings, which
// stored the register number + hardware on each physical machine). This is the
// device's local identity: which register lane it is, and which receipt printer
// / cash-drawer transport to use. It is persisted in localStorage so a terminal
// "signs into" a register once and remembers it across reloads.
//
// The Stripe card reader is intentionally NOT stored here — it is looked up from
// the `registers` table by register number, so a reader can be re-assigned to a
// lane centrally without touching the device.
// =============================================================================

export type PrinterType = 'serial' | 'browser'
export type DrawerType = 'serial' | 'none'

export interface DeviceRegisterProfile {
  registerNumber: number
  registerName?: string | null
  printerType: PrinterType
  drawerType: DrawerType
}

const KEY = 'thomsons.device.register'

// Simple subscription so the UI (top bar, settings) reacts to sign-in changes.
type Listener = (p: DeviceRegisterProfile | null) => void
const listeners = new Set<Listener>()

export function getDeviceRegister(): DeviceRegisterProfile | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as DeviceRegisterProfile
    if (typeof p.registerNumber !== 'number') return null
    return {
      registerNumber: p.registerNumber,
      registerName: p.registerName ?? null,
      printerType: p.printerType ?? 'serial',
      drawerType: p.drawerType ?? 'serial',
    }
  } catch {
    return null
  }
}

export function setDeviceRegister(profile: DeviceRegisterProfile): void {
  localStorage.setItem(KEY, JSON.stringify(profile))
  listeners.forEach(l => l(profile))
}

export function clearDeviceRegister(): void {
  localStorage.removeItem(KEY)
  listeners.forEach(l => l(null))
}

export function onDeviceRegisterChange(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
